import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import useDefaultModal from '../hooks/useDefaultModal';
import DefaultModal from '../modals/DefaultModal';

const RendimentosJsonUploadModal = ({ show, onClose, onSuccess, contas = [] }) => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const { setDefaultModal, showDefaultModal } = useDefaultModal();
    const fileInputRef = useRef(null);
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const ano = localStorage.getItem('ano');

    const mesesValidos = ['janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho', 
                          'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const resetModalState = () => {
        setFile(null);
        setProgress({ current: 0, total: 0 });
        setIsProcessing(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validarEstrutura = (data) => {
        if (!data.rendimentos || !Array.isArray(data.rendimentos)) {
            throw new Error('JSON deve conter um array "rendimentos"');
        }

        data.rendimentos.forEach((rendimento, index) => {
            if (!rendimento.conta) {
                throw new Error(`Rendimento no índice ${index} não possui campo "conta"`);
            }

            const temMeses = mesesValidos.some(mes => rendimento.hasOwnProperty(mes));
            if (!temMeses) {
                throw new Error(`Rendimento no índice ${index} não possui nenhum campo de mês válido`);
            }
        });

        return true;
    };

    const encontrarConta = (numConta) => {
        // Busca pela conta com match exato (string comparison com trim)
        return contas.find(conta => 
            String(conta.numConta).trim() === String(numConta).trim()
        );
    };

    /**
     * Obtém idPrograma da conta.
     * Prioridade 1: usar idPrograma do array contas se existir
     * Prioridade 2: consultar backend se não tiver
     */
    const obterIdPrograma = async (contaEncontrada) => {
        // Prioridade 1: usar idPrograma da conta se já carregado
        if (contaEncontrada && contaEncontrada.idPrograma) {
            return contaEncontrada.idPrograma;
        }

        // Prioridade 2: se não tiver, consultar backend
        // (não deveria chegar aqui se contas está carregado corretamente)
        console.warn(`Conta ${contaEncontrada?.numConta} não possui idPrograma. Consultando backend...`);
        
        try {
            // Query pela conta usando escola + numConta
            const response = await axios.get(
                `${API_BASE_URL.CONTA}/findByEscolaAndPrograma`,
                {
                    params: {
                        idEscola: cnpj,
                        idPrograma: 0 // Este endpoint talvez não seja o ideal, ver alternativa
                    },
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            // Buscar a conta específica na resposta
            const contaBackend = response.data?.find(
                c => String(c.numConta).trim() === String(contaEncontrada?.numConta).trim()
            );
            
            if (contaBackend && contaBackend.idPrograma) {
                console.log(`IdPrograma obtido do backend: ${contaBackend.idPrograma}`);
                return contaBackend.idPrograma;
            }
        } catch (error) {
            console.warn(`Erro ao consultar backend para idPrograma:`, error.message);
        }

        // Se chegou aqui, não conseguiu obter idPrograma
        return null;
    };

    const extrairMeses = (rendimento) => {
        const meses = {};
        mesesValidos.forEach(mes => {
            const valor = rendimento[mes];
            meses[mes] = parseFloat(valor) || 0;
        });
        return meses;
    };

    const handleUpload = async () => {
        if (!file) {
            showDefaultModal('error', 'Selecione um arquivo JSON para upload.');
            return;
        }

        if (!contas || contas.length === 0) {
            showDefaultModal('error', 'Nenhuma conta disponível. Recarregue a página e tente novamente.');
            return;
        }

        if (!token || !cnpj || !ano) {
            showDefaultModal('error', 'Dados de sessão incompletos. Recarregue a página.');
            return;
        }

        try {
            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const jsonData = JSON.parse(event.target.result);
                    validarEstrutura(jsonData);

                    setIsProcessing(true);
                    const rendimentos = jsonData.rendimentos;
                    setProgress({ current: 0, total: rendimentos.length });

                    let sucessos = 0;
                    let pulados = 0;
                    const contasPuladas = [];

                    for (let i = 0; i < rendimentos.length; i++) {
                        const rendimento = rendimentos[i];
                        const contaEncontrada = encontrarConta(rendimento.conta);

                        if (!contaEncontrada) {
                            console.warn(`Conta ${rendimento.conta} não encontrada no sistema`);
                            contasPuladas.push(`${rendimento.conta} (não existe)`);
                            pulados++;
                            setProgress({ current: i + 1, total: rendimentos.length });
                            continue;
                        }

                        // Obter/validar idPrograma
                        const idPrograma = await obterIdPrograma(contaEncontrada);
                        if (!idPrograma) {
                            console.warn(`Conta ${rendimento.conta} não possui idPrograma associado`);
                            contasPuladas.push(`${rendimento.conta} (sem programa)`);
                            pulados++;
                            setProgress({ current: i + 1, total: rendimentos.length });
                            continue;
                        }

                        const meses = extrairMeses(rendimento);
                        const payload = {
                            ano: parseInt(ano),
                            idPrograma: idPrograma,
                            idEscola: cnpj,
                            idConta: contaEncontrada.numConta,
                            ...meses
                        };

                        try {
                            console.log('Enviando rendimento:', {
                                conta: rendimento.conta,
                                programa: payload.idPrograma,
                                escola: payload.idEscola,
                                ano: payload.ano
                            });
                            
                            await axios.post(`${API_BASE_URL.RENDIMENTO}/save`, payload, {
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'application/json',
                                },
                            });
                            sucessos++;
                            console.log(`✓ Rendimento importado: ${rendimento.conta}`);
                        } catch (error) {
                            console.error(
                                `✗ Erro ao salvar rendimento para conta ${rendimento.conta}:`,
                                error.response?.data || error.message
                            );
                            pulados++;
                            contasPuladas.push(`${rendimento.conta} (erro ao salvar)`);
                        }

                        setProgress({ current: i + 1, total: rendimentos.length });
                    }

                    setIsProcessing(false);
                    resetModalState();
                    onClose();

                    // Montar mensagem de resumo
                    let mensagem = `${sucessos} rendimento(s) importado(s) com sucesso`;
                    if (pulados > 0) {
                        mensagem += ` - ${pulados} pulado(s)`;
                        if (contasPuladas.length > 0 && contasPuladas.length <= 5) {
                            mensagem += `: ${contasPuladas.join('; ')}`;
                        } else if (contasPuladas.length > 5) {
                            mensagem += `: ${contasPuladas.slice(0, 3).join('; ')}... (+${contasPuladas.length - 3} mais)`;
                        }
                    }
                    
                    showDefaultModal('success', mensagem);

                    if (onSuccess) onSuccess();
                } catch (error) {
                    console.error('Erro ao processar JSON:', error);
                    setIsProcessing(false);
                    showDefaultModal('error', `Erro ao processar arquivo: ${error.message}`);
                }
            };

            reader.readAsText(file);
        } catch (error) {
            console.error('Erro ao ler arquivo:', error);
            showDefaultModal('error', 'Erro ao ler arquivo.');
        }
    };

    useEffect(() => {
        if (!show) {
            resetModalState();
        }
    }, [show]);

    const handleClose = () => {
        if (!isProcessing) {
            resetModalState();
            onClose();
        }
    };

    return (
        <>
            <div
                className={`modal fade ${show ? 'show' : ''}`}
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
                style={{ display: show ? 'block' : 'none' }}
                inert={!show ? '' : undefined}
            >
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title text-secondary text-shadow">IMPORTAR RENDIMENTOS (JSON)</h5>
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={handleClose}
                                disabled={isProcessing}
                            ></button>
                        </div>
                        <div className="modal-body">
                            {!isProcessing ? (
                                <>
                                    <p className="text-muted small mb-3">
                                        Selecione um arquivo JSON com a seguinte estrutura:
                                    </p>
                                    <pre className="bg-light p-2 rounded small mb-3" style={{ fontSize: '0.75rem', maxHeight: '150px', overflowY: 'auto' }}>
{`{
  "rendimentos": [
    {
      "conta": "5433-X",
      "janeiro": 1.08,
      "fevereiro": 1.08,
      ...
      "dezembro": 1.0
    }
  ]
}`}
                                    </pre>
                                    <div className="alert alert-info small mb-3">
                                        <strong>ℹ️ Dica:</strong> Use exatamente o número/código da conta que aparece na tabela de Rendimentos
                                    </div>
                                    <input
                                        type="file"
                                        accept=".json"
                                        className="form-control"
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                        disabled={isProcessing}
                                    />
                                </>
                            ) : (
                                <div className="text-center">
                                    <div className="spinner-border text-primary mb-3" role="status">
                                        <span className="visually-hidden">Processando...</span>
                                    </div>
                                    <p className="mb-2 fw-bold">
                                        Processando: {progress.current} de {progress.total}
                                    </p>
                                    <div className="progress" style={{ height: '25px' }}>
                                        <div 
                                            className="progress-bar bg-success" 
                                            role="progressbar"
                                            style={{ 
                                                width: `${progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%`,
                                                transition: 'width 0.3s ease'
                                            }}
                                            aria-valuenow={progress.current} 
                                            aria-valuemin="0" 
                                            aria-valuemax={progress.total}
                                        >
                                            {progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={handleClose} 
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Processando...' : 'Cancelar'}
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-primary" 
                                onClick={handleUpload} 
                                disabled={!file || isProcessing}
                            >
                                {isProcessing ? 'Importando...' : 'Importar'}
                            </button>
                        </div>
                    </div>
                </div>
                <DefaultModal {...setDefaultModal} />
            </div>

            {show && <div className="modal-backdrop fade show"></div>}
        </>
    );
};

export default RendimentosJsonUploadModal;
